import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { Button, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { getDriverWorkContext, type DriverWorkContext } from '../../src/api/apiService';
import { WorkCompanyForm } from '../../src/components/WorkCompanyForm';
import { WorkContextSummary } from '../../src/components/WorkContextSummary';

export default function WorkCompanyPage() {
  const router = useRouter();
  const theme = useTheme();
  const { t } = useTranslation('workCompany');
  const [context, setContext] = useState<DriverWorkContext | null>(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    setError(false);
    try {
      const result = await getDriverWorkContext();
      if (!mounted.current) return;
      if (!result.ok || !result.data) { setError(true); return; }
      setContext(result.data as DriverWorkContext);
      setEditing(false);
    } catch {
      if (mounted.current) setError(true);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, [load]);
  return <ScrollView contentContainerStyle={{ padding: 24, gap: 24 }} style={{ backgroundColor: theme.colors.background }}>
    {error ? <>
      <Text accessibilityRole="alert">{t('retry', 'Unable to validate the work company. Retry or contact support.')}</Text>
      <Button onPress={() => void load()}>{t('retryAction', 'Retry')}</Button>
    </> : !context ? <Text>{t('loading', 'Loading work context')}</Text> : <>
      <WorkContextSummary context={context} />
      {context.work_company && !editing && ['self', 'company'].includes(context.license.payer || '') && context.pending_reason !== 'work_relationship_unresolved' && <>
        <Button onPress={() => setEditing(true)}>{t('change', 'Change work company')}</Button>
      </>}
      {(editing || !context.work_company) && context.license.payer === 'company' && <>
        <Text>{t('corporateChange', 'To retain company funding, ask the destination company for a company-funded invitation. This declaration cannot change your payer.')}</Text>
      </>}
      {(context.pending_reason === 'work_company_required' || (editing && context.license.payer === 'self')) && <WorkCompanyForm
        expectedRelationshipId={context.relationship_id} onSuccess={() => void load()} />}
    </>}
    <Button onPress={() => router.replace('/dashboard/home')}>{t('return', 'Return to work')}</Button>
  </ScrollView>;
}
